import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Avatar } from '../../shared/ui/Avatar';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

type Ref = { personId: string; name: string };
// Solo tres para temas nuevos; los de Testimonios y Avisos de antes siguen visibles en «Todo».
const CATEGORIES: [string, string][] = [['general', 'General'], ['estudio', 'Estudio bíblico'], ['preguntas', 'Preguntas']];
const OLD: Record<string, string> = { cultos: 'Cultos', testimonios: 'Testimonios', avisos: 'Avisos' };
const cat = (k: string) => CATEGORIES.find(([x]) => x === k)?.[1] ?? OLD[k] ?? k;
const when = (iso: string) => new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** Foro: temas por categoría. */
export function ForumPage() {
  const me = useMe();
  const base = `/communication/churches/${me.activeChurchId}/forum`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canModerate: boolean; openReports: number | null; items: { id: string; category: string; title: string; author: Ref; pinned: boolean; closed: boolean; hidden: boolean; lastPostAt: string; replies: number }[] }>(base, signal) : Promise.resolve(null)), [base]);
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  const items = data.status === 'ready' && data.data ? data.data.items.filter((t) => !filter || t.category === filter) : [];
  return (
    <div className="page">
      <header className="page__header">
        <h1>Foro</h1>
        <p className="muted">Conversaciones de la iglesia por tema. Sé respetuoso: Pastoral modera.</p>
      </header>
      {data.status === 'ready' && data.data?.openReports ? <Alert tone="info">Hay {data.data.openReports} reporte(s) por revisar.</Alert> : null}
      {!open ? <div><Button onClick={() => setOpen(true)}>Nuevo tema</Button></div> : (
        <Card title="Nuevo tema">
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              setBusy(true);
              setError(null);
              api.post<{ id: string }>(base, { category: String(f.get('category')), title: String(f.get('title') ?? '').trim(), body: String(f.get('body') ?? '').trim() })
                .then((r) => navigate(`/app/foro/${r.id}`))
                .catch((err) => { setError(errorMessage(err)); setBusy(false); });
            }}
          >
            {error ? <Alert>{error}</Alert> : null}
            <div className="field">
              <label htmlFor="fo-cat">Categoría</label>
              <select id="fo-cat" name="category" defaultValue="general">{CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            </div>
            <Field label="Título" name="title" required minLength={3} maxLength={150} />
            <div className="field"><label htmlFor="fo-body">Mensaje</label><textarea id="fo-body" name="body" rows={4} required maxLength={5000} /></div>
            <div className="actions"><Button type="submit" busy={busy}>Publicar</Button><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button></div>
          </form>
        </Card>
      )}
      <div className="tabs tabs--scroll" role="tablist" aria-label="Categoría">
        {[['', 'Todo'] as [string, string], ...CATEGORIES].map(([k, l]) => (
          <button key={k || 'all'} type="button" role="tab" aria-selected={filter === k} className={`tabs__tab${filter === k ? ' tabs__tab--active' : ''}`} onClick={() => setFilter(k)}>{l}</button>
        ))}
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && items.length === 0 ? <Card><EmptyState title="Todavía no hay temas aquí." /></Card> : null}
      {items.length ? (
        <ul className="list card">
          {items.map((t) => (
            <li key={t.id} className="list__item">
              <Link className="list__link" to={`/app/foro/${t.id}`}>
                <strong>{t.pinned ? '📌 ' : ''}{t.title}{t.closed ? ' (cerrado)' : ''}{t.hidden ? ' (oculto)' : ''}</strong>
                <span className="small muted">{cat(t.category)} · {t.author.name} · {t.replies} {t.replies === 1 ? 'respuesta' : 'respuestas'} · {when(t.lastPostAt)}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

type Topic = {
  id: string; category: string; title: string; body: string; author: Ref; pinned: boolean; closed: boolean; hidden: boolean; createdAt: string; canModerate: boolean; mine: boolean;
  posts: { id: string; author: Ref; body: string; hidden: boolean; createdAt: string; mine: boolean }[];
  reports: { id: string; postId: string | null; reason: string | null }[];
};

export function ForumTopicPage() {
  const { topicId = '' } = useParams();
  const me = useMe();
  const url = `/communication/churches/${me.activeChurchId}/forum/${topicId}`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Topic>(url, signal) : Promise.resolve(null)), [url]);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} /></div>;
  const t = data.data;
  if (!t) return null;
  const run = async (action: () => Promise<unknown>, ok: string | null) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      data.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };
  const mod = (b: object, ok: string) => void run(() => api.put(`${url}/moderate`, b), ok);
  const report = (postId: string | null) => {
    const reason = window.prompt('¿Por qué lo reportas? (opcional)');
    if (reason !== null) void run(() => api.post(`${url}/report`, { postId, reason: reason || null }), 'Gracias. Pastoral lo revisará.');
  };
  return (
    <div className="page page--narrow">
      <p className="small"><Link to="/app/foro">← Foro</Link></p>
      <header className="page__header">
        <h1>{t.title}</h1>
        <p className="muted">{cat(t.category)}{t.closed ? ' · cerrado' : ''}{t.hidden ? ' · oculto' : ''}</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {t.canModerate ? (
        <Card title="Moderación">
          {t.reports.length ? <Alert tone="info">{t.reports.length} reporte(s): {t.reports.map((r) => r.reason ?? 'sin motivo').join(' · ')}</Alert> : null}
          <div className="actions actions--wrap">
            <Button variant="secondary" busy={busy} onClick={() => mod({ pin: !t.pinned }, t.pinned ? 'Desfijado.' : 'Fijado.')}>{t.pinned ? 'Desfijar' : 'Fijar'}</Button>
            <Button variant="secondary" busy={busy} onClick={() => mod({ close: !t.closed }, t.closed ? 'Reabierto.' : 'Cerrado.')}>{t.closed ? 'Reabrir' : 'Cerrar'}</Button>
            <Button variant="ghost" busy={busy} onClick={() => mod({ hide: !t.hidden }, t.hidden ? 'Visible otra vez.' : 'Ocultado.')}>{t.hidden ? 'Mostrar' : 'Ocultar tema'}</Button>
            {t.reports.length ? <Button variant="ghost" busy={busy} onClick={() => mod({ resolveReports: true }, 'Reportes resueltos.')}>Marcar reportes como resueltos</Button> : null}
          </div>
        </Card>
      ) : null}
      <Card>
        <div className="avatar-row"><Avatar personId={t.author.personId} name={t.author.name} size={32} /><span className="small muted">{t.author.name} · {when(t.createdAt)}</span></div>
        <p className="pre-line">{t.body}</p>
        {!t.mine ? <Button variant="ghost" onClick={() => report(null)}>Reportar</Button> : null}
      </Card>
      {t.posts.map((p) => (
        <Card key={p.id}>
          <div className="avatar-row"><Avatar personId={p.author.personId} name={p.author.name} size={28} /><span className="small muted">{p.author.name} · {when(p.createdAt)}{p.hidden ? ' · oculta' : ''}</span></div>
          <p className="pre-line">{p.body}</p>
          <div className="actions actions--wrap">
            {!p.mine ? <Button variant="ghost" onClick={() => report(p.id)}>Reportar</Button> : <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Borrar tu respuesta?')) void run(() => api.delete(`${url}/posts/${p.id}`), 'Borrada.'); }}>Borrar</Button>}
            {t.canModerate ? <Button variant="ghost" busy={busy} onClick={() => mod(p.hidden ? { showPostId: p.id } : { hidePostId: p.id }, p.hidden ? 'Visible.' : 'Ocultada.')}>{p.hidden ? 'Mostrar' : 'Ocultar'}</Button> : null}
          </div>
        </Card>
      ))}
      {!t.closed && !t.hidden ? (
        <Card title="Responder">
          <form className="form" onSubmit={(e) => { e.preventDefault(); const form = e.currentTarget; const body = String(new FormData(form).get('body') ?? '').trim(); if (body) void run(() => api.post(`${url}/posts`, { body }), null).then((ok) => ok && form.reset()); }}>
            <div className="field"><label htmlFor="fo-reply">Tu respuesta</label><textarea id="fo-reply" name="body" rows={3} maxLength={5000} required /></div>
            <Button type="submit" busy={busy}>Responder</Button>
          </form>
        </Card>
      ) : <p className="small muted">Este tema está cerrado.</p>}
    </div>
  );
}
